/**
 * Tenant WebAuthn / passkey hooks (WA-2 — tenant fast-follow to the SA
 * WA-1a/WA-1b stack).
 *
 * Six endpoints, all under /api/t/{slug}/auth/mfa/webauthn/:
 *
 *   POST   .../enroll/begin    — dual-mode (mfa_pending OR tenant access JWT)
 *   POST   .../enroll/finish   — dual-mode
 *   POST   .../verify/begin    — mfa_pending JWT only
 *   POST   .../verify/finish   — mfa_pending JWT only
 *   GET    .../credentials     — tenant access JWT
 *   DELETE .../credentials/{id} — tenant access JWT + password
 *
 * Mirrors ``useAdminWebauthn.ts`` verbatim but tenant-flavored: the base path
 * carries the tenant slug, and the response types are re-exported from the
 * admin hook (the backend schemas are shared — ``WebauthnBeginResponse`` /
 * ``AdminMfaVerifyResponse`` / ``WebauthnCredentialItem``).
 *
 * Auth header note (same as the SA hook):
 *   - Enroll begin/finish: ``pendingOrAccessHeader`` — mfa_pending first
 *     (bootstrap from a login that required MFA), else the tenant access JWT
 *     (in-Settings self-enable). Backend ``_require_tenant_pending_or_access``
 *     accepts both.
 *   - Verify begin/finish: ``mfaPendingHeader`` — mfa_pending JWT only.
 *   - GET / DELETE: apiClient interceptor adds the tenant access JWT.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";
import type {
  WebauthnBeginResponse,
  WebauthnFinishResponse,
  WebauthnCredentialItem,
} from "@/hooks/queries/admin/useAdminWebauthn";

// Re-export the row type — Settings/Security.tsx consumes it. The begin/finish
// response types are shared verbatim with the SA hook (structural typing lets
// the shared PasskeyEnrollFlow / PasskeyVerifyFlow accept our mutations).
export type { WebauthnCredentialItem } from "@/hooks/queries/admin/useAdminWebauthn";

interface WebauthnCredentialsListResponse {
  items: WebauthnCredentialItem[];
}

// ── Auth helpers ──────────────────────────────────────────────────────────────

/** Enroll: prefer the mfa_pending token (bootstrap), fall back to the tenant
 * access token (in-Settings self-enable). Backend accepts both shapes. */
function pendingOrAccessHeader(): { Authorization: string } {
  const { mfaPendingToken, accessToken } = useAuthStore.getState();
  const token = mfaPendingToken ?? accessToken;
  if (!token) {
    throw new Error("No auth token available for WebAuthn enroll");
  }
  return { Authorization: `Bearer ${token}` };
}

/** Verify: mfa_pending JWT only. */
function mfaPendingHeader(): { Authorization: string } {
  const token = useAuthStore.getState().mfaPendingToken;
  if (!token) {
    throw new Error("mfaPendingToken missing from store");
  }
  return { Authorization: `Bearer ${token}` };
}

// ── Query keys ────────────────────────────────────────────────────────────────

function credentialsKey(slug: string) {
  return ["tenant", slug, "webauthn", "credentials"] as const;
}

const base = (slug: string) =>
  `/api/t/${encodeURIComponent(slug)}/auth/mfa/webauthn`;

// ── Hooks ─────────────────────────────────────────────────────────────────────

/** POST .../enroll/begin — registration options (dual-mode auth). */
export function useTenantWebauthnEnrollBegin(slug: string) {
  return useMutation<WebauthnBeginResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<WebauthnBeginResponse>(
        `${base(slug)}/enroll/begin`,
        undefined,
        { headers: pendingOrAccessHeader() },
      );
      return resp.data;
    },
  });
}

/** POST .../enroll/finish — submit attestation + optional nickname. */
export function useTenantWebauthnEnrollFinish(slug: string) {
  return useMutation<
    WebauthnFinishResponse,
    ApiError,
    { credential: Record<string, unknown>; nickname?: string | null }
  >({
    mutationFn: async ({ credential, nickname }) => {
      const resp = await apiClient.post<WebauthnFinishResponse>(
        `${base(slug)}/enroll/finish`,
        { credential, nickname: nickname ?? null },
        { headers: pendingOrAccessHeader() },
      );
      return resp.data;
    },
  });
}

/** POST .../verify/begin — assertion options (mfa_pending JWT). */
export function useTenantWebauthnVerifyBegin(slug: string) {
  return useMutation<WebauthnBeginResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<WebauthnBeginResponse>(
        `${base(slug)}/verify/begin`,
        undefined,
        { headers: mfaPendingHeader() },
      );
      return resp.data;
    },
  });
}

/** POST .../verify/finish — submit assertion (mfa_pending JWT). */
export function useTenantWebauthnVerifyFinish(slug: string) {
  return useMutation<
    WebauthnFinishResponse,
    ApiError,
    { credential: Record<string, unknown> }
  >({
    mutationFn: async ({ credential }) => {
      const resp = await apiClient.post<WebauthnFinishResponse>(
        `${base(slug)}/verify/finish`,
        { credential },
        { headers: mfaPendingHeader() },
      );
      return resp.data;
    },
  });
}

/** GET .../credentials — list the tenant user's passkeys (access JWT via interceptor). */
export function useTenantWebauthnCredentials(slug: string, enabled = true) {
  return useQuery<WebauthnCredentialsListResponse, ApiError>({
    queryKey: credentialsKey(slug),
    queryFn: async () => {
      const resp = await apiClient.get<WebauthnCredentialsListResponse>(
        `${base(slug)}/credentials`,
      );
      return resp.data;
    },
    enabled: enabled && !!slug,
    staleTime: 30_000,
  });
}

/** DELETE .../credentials/{id} — password-only (no TOTP). */
export function useDeleteTenantWebauthnCredential(slug: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiError,
    { credential_id: string; current_password: string }
  >({
    mutationFn: async ({ credential_id, current_password }) => {
      await apiClient.delete(`${base(slug)}/credentials/${credential_id}`, {
        data: { current_password },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: credentialsKey(slug) });
    },
  });
}
